import { useCallback, useState } from 'react'

import {
  type Attempt,
  type Exam,
  addExam,
  fetchAttempt,
  fetchAttempts,
  fetchExams,
  fetchMarkingSchemes,
  removeAttempt,
  removeExam,
  saveExam,
  startAttempt,
} from '../api/exams'
import { asFailure, type Failure } from '../api/request'
import { fetchSubjects } from '../api/subjects'
import { useLoad } from '../api/useLoad'
import { AREA_IDS, ENTRY_AREA_ID } from '../frame/areas'
import { Lanes } from '../frame/Lanes'
import { navigate, useRoute } from '../frame/route'
import { useAnnounce } from '../ui/Announce'
import { Button } from '../ui/Control'
import { Offer } from '../ui/Offer'
import { Failed, Waiting } from '../ui/Status'
import { ExamEditor } from './exams/ExamEditor'
import { OpenExam } from './exams/OpenExam'
import { attemptPath, EXAMS_AREA, openedIn } from './exams/place'
import { Sitting } from './exams/Sitting'

/**
 * Tests: what somebody sets themselves to find out whether they can actually write it down.
 *
 * The area exists because the flashcards answer a different question. A card asks what is answered
 * in a sentence and marks itself; this asks for an essay, an opinion, a sketched family tree — and
 * **MyLib deliberately forms no judgement about any of it**. It keeps the paper, the answers, and the
 * standard each task is measured by, and the judging is asked of an assistant afterwards.
 *
 * Three screens and one address each: the tests, one test with its sittings, and a sitting — the
 * split screen where the question stands beside what is being written.
 */
export function Exams() {
  const route = useRoute(AREA_IDS, ENTRY_AREA_ID)
  const opened = openedIn(route.path)
  const announce = useAnnounce()

  const exams = useLoad(useCallback((signal: AbortSignal) => fetchExams(signal), []))
  const subjects = useLoad(useCallback((signal: AbortSignal) => fetchSubjects(false, signal), []))
  const schemes = useLoad(useCallback((signal: AbortSignal) => fetchMarkingSchemes(signal), []))

  const attemptId = opened.attemptId
  const sitting = useLoad(
    useCallback(
      async (signal: AbortSignal) => (attemptId === null ? null : fetchAttempt(attemptId, signal)),
      [attemptId],
    ),
  )

  const examId = opened.examId
  const sittings = useLoad(
    useCallback(
      async (signal: AbortSignal) => (examId === null ? [] : fetchAttempts(examId, signal)),
      [examId],
    ),
  )

  const [refused, setRefused] = useState<Failure | null>(null)
  // What the sitting screen holds while somebody writes: the answers come back from every save, and
  // asking the list again for each of them would re-read the editor under the cursor.
  const [written, setWritten] = useState<Attempt | null>(null)

  const all = exams.load.status === 'ready' ? exams.load.value : []
  const open = all.find((exam) => exam.id === opened.examId) ?? null
  const attempt = written ?? (sitting.load.status === 'ready' ? sitting.load.value : null)
  const examOfSitting =
    attempt === null ? null : (all.find((one) => one.id === attempt.examId) ?? null)

  const go = (path: readonly string[] = []) => {
    navigate(EXAMS_AREA, path)
  }

  const attempt_ = async (work: () => Promise<void>) => {
    try {
      setRefused(null)
      await work()
    } catch (error) {
      setRefused(asFailure(error))
    }
  }

  const overview = (
    <div className="exams">
      <header className="exams__head">
        <h1 className="exams__title">Tests</h1>
        <Button
          tone="primary"
          icon="plus"
          onClick={() => {
            go(['new'])
          }}
        >
          Create a test
        </Button>
      </header>

      {refused !== null && <Failed failure={refused} />}

      {exams.load.status === 'waiting' && <Waiting label="Fetching the tests" />}
      {exams.load.status === 'failed' && (
        <Failed failure={exams.load.failure} onRetry={exams.retry} />
      )}

      {exams.load.status === 'ready' &&
        (all.length === 0 ? (
          <Offer
            title="Nothing set yet"
            lead="A test is a paper you set yourself: one or more tasks, each measured by a standard you wrote at its subject."
            points={[
              'A task is pages of a script you already keep, or a question you write here',
              'Sitting it puts the question beside what you write, and keeps what you wrote',
              'An assistant reads a sitting afterwards and says what was good and what was not',
            ]}
            action={
              <Button
                tone="primary"
                onClick={() => {
                  go(['new'])
                }}
              >
                Create the first one
              </Button>
            }
          />
        ) : (
          <ul className="exams__list">
            {all.map((exam) => (
              <li className="exams__entry" key={exam.id}>
                <button
                  className="exam-tile"
                  type="button"
                  aria-current={exam.id === opened.examId ? 'true' : undefined}
                  onClick={() => {
                    go([exam.id])
                  }}
                >
                  <span className="exam-tile__title">{exam.title}</span>
                  <span className="exam-tile__about">
                    {exam.tasks.length === 1 ? '1 task' : `${String(exam.tasks.length)} tasks`}
                    {exam.subjectName !== null && ` · ${exam.subjectName}`}
                  </span>
                  <span className="exam-tile__sittings">
                    {exam.attempts === 0
                      ? 'Not sat yet'
                      : exam.attempts === 1
                        ? 'Sat once'
                        : `Sat ${String(exam.attempts)} times`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ))}
    </div>
  )

  const detail = (() => {
    if (opened.attemptId !== null) {
      if (sitting.load.status === 'waiting' || exams.load.status === 'waiting') {
        return <Waiting label="Fetching the sitting" />
      }
      if (sitting.load.status === 'failed') {
        return <Failed failure={sitting.load.failure} onRetry={sitting.retry} />
      }
      if (attempt === null || examOfSitting === null) {
        return <p className="exams__gone">That sitting is not there any more.</p>
      }
      return (
        <Sitting
          exam={examOfSitting}
          attempt={attempt}
          onChanged={setWritten}
          onClose={() => {
            setWritten(null)
            go([examOfSitting.id])
          }}
        />
      )
    }

    if (opened.creating || (opened.editing && open !== null)) {
      return (
        <ExamEditor
          exam={opened.creating ? null : open}
          subjects={subjects.load.status === 'ready' ? subjects.load.value : []}
          schemes={schemes.load.status === 'ready' ? schemes.load.value : []}
          onSave={async (title, subjectId, tasks) => {
            await attempt_(async () => {
              const saved: Exam = opened.creating
                ? await addExam(title, subjectId, tasks)
                : await saveExam(open?.id ?? '', title, subjectId, tasks)
              exams.retry()
              announce('Saved.')
              go([saved.id])
            })
          }}
          onCancel={() => {
            go(opened.creating || open === null ? [] : [open.id])
          }}
        />
      )
    }

    if (open === null) {
      return null
    }

    return (
      <OpenExam
        exam={open}
        attempts={sittings.load.status === 'ready' ? sittings.load.value : []}
        onEdit={() => {
          go([open.id, 'edit'])
        }}
        onSit={() => {
          void attempt_(async () => {
            const started = await startAttempt(open.id)
            exams.retry()
            setWritten(null)
            go(attemptPath(started.id))
          })
        }}
        onOpenAttempt={(id) => {
          setWritten(null)
          go(attemptPath(id))
        }}
        onRemoveAttempt={(id) => {
          void attempt_(async () => {
            await removeAttempt(id)
            sittings.retry()
            exams.retry()
            announce('The sitting is gone.')
          })
        }}
        onRemove={() => {
          void attempt_(async () => {
            await removeExam(open.id)
            exams.retry()
            announce('The test is gone.')
            go()
          })
        }}
        onClose={() => {
          go()
        }}
      />
    )
  })()

  return (
    <Lanes
      overview={{ label: 'The tests', content: overview }}
      detail={detail === null ? null : { label: 'What is open', content: detail }}
      full={opened.attemptId !== null}
    />
  )
}
